'use client';

import { IReviewsData, IReviewsItem } from '@/api/types/product';
import { IGoodsDetail, IProductDetail } from '@/api/types/public';
import Breadcrumbs from '@/components/Breadcrumbs';
import TopCarousel from '@/components/GoodsDetail/TopCarousel';
import { useStore } from '@/providers/app-store-provider';
import { colors, font } from '@/theme/zeelool.theme';
import { Box, IconButton, Typography } from '@mui/material';
import { LeftArrowOutlined } from '@zeelool/icon';
import dynamic from 'next/dynamic';
import React, { useCallback, useState, useEffect } from 'react';
import BottomButton from './BottomButton';

import { IProductDetailsPageInteractionBtnClickParams } from './CustomerReviewsItem';
import CustomerReviewsSkeleton, {
  WapCollapseSkeleton,
  WapProductDetailsSkeleton,
} from './CustomerReviewsSkeleton';
import ProductInfo from './ProductInfo/m';
import { useTranslations } from '@/i18n/use-translations';
import ProductInfoGlasses from './ProductInfo/glasses.m';

function CustomerReviewsLoadingSkeleton() {
  return (
    <section id="customer-reviews-list" className="bg-white">
      <section className="mx-auto border-t border-gray-200 px-[14px] py-[16px]">
        <div className="mb-[16px] h-[18px] w-[132px] animate-pulse rounded bg-[#f2f2f2]" />
        <CustomerReviewsSkeleton count={1} />
      </section>
    </section>
  );
}

const Popup = dynamic(() => import('@/components/Popup'), { ssr: true });
const PopupClientOnly = dynamic(() => import('@/components/Popup'), { ssr: false });
const CustomerReviewsList = dynamic(() => import('./CustomerReviewsList'), { ssr: true });
const CustomerReviewsListClientOnly = dynamic(() => import('./CustomerReviewsList'), {
  ssr: false,
});
const CustomerReviews = dynamic(() => import('./CustomerReviews'), { ssr: true });
const CustomerReviewsClientOnly = dynamic(() => import('./CustomerReviews'), {
  ssr: false,
  loading: () => <CustomerReviewsLoadingSkeleton />,
});
const LensesAndCoatingsWithPopup = dynamic(
  () => import('@/components/GoodsDetail/LensesAndCoatingsWithPopup'),
  { ssr: true },
);
const LensesAndCoatingsWithPopupClientOnly = dynamic(
  () => import('@/components/GoodsDetail/LensesAndCoatingsWithPopup'),
  { ssr: false, loading: () => <WapCollapseSkeleton itemCount={8} /> },
);
const ServicesGuarantees = dynamic(() => import('@/components/GoodsDetail/ServicesGuarantees'), {
  ssr: true,
});
const ServicesGuaranteesClientOnly = dynamic(
  () => import('@/components/GoodsDetail/ServicesGuarantees'),
  {
    ssr: false,
    loading: () => <WapCollapseSkeleton itemCount={5} />,
  },
);
const ProductDetailsWithPopup = dynamic(
  () => import('@/components/GoodsDetail/ProductDetailsWithPopup'),
  { ssr: true },
);
const ProductDetailsWithPopupClientOnly = dynamic(
  () => import('@/components/GoodsDetail/ProductDetailsWithPopup'),
  { ssr: false, loading: () => <WapProductDetailsSkeleton /> },
);
const FreeGift = dynamic(() => import('@/components/GoodsDetail/FreeGift'), { ssr: true });
const FreeGiftClientOnly = dynamic(() => import('@/components/GoodsDetail/FreeGift'), {
  ssr: false,
  loading: () => <WapCollapseSkeleton itemCount={3} />,
});
const ProductDescriptionCollapse = dynamic(
  () => import('@/components/GoodsDetail/ProductDescriptionCollapse'),
  { ssr: true },
);
const ProductDescriptionCollapseClientOnly = dynamic(
  () => import('@/components/GoodsDetail/ProductDescriptionCollapse'),
  { ssr: false, loading: () => <WapCollapseSkeleton itemCount={4} /> },
);
const ProductRichText = dynamic(() => import('./WapProductRichText'), {
  ssr: true,
});
const ProductRichTextClientOnly = dynamic(() => import('./WapProductRichText'), {
  ssr: false,
});

interface WapGoodsDetailProps {
  detail: {
    product: IProductDetail;
    customer: IGoodsDetail['customer'];
    select_lens_config: IGoodsDetail['select_lens_config'];
  };
  showTabBtn: boolean;
  reviewsData: IReviewsData;
  featuredReview?: IReviewsItem;
  reviewImagesRes?: {
    data: Array<{
      image_show?: string;
      review_id?: number;
      star?: number | string;
      title?: string;
      detail?: string;
      [key: string]: any;
    }>;
    total?: number;
  };
  shapePath: { id: string; name: string } | null;
  materialPath: { id: string; name: string } | null;
  getGlassesFilter: string;
  // 状态控制
  reviewDialog: boolean;
  setReviewDialog: (open: boolean) => void;
  // 加载状态
  addCartLoading: boolean;
  frameOnlyLoading: boolean;
  selectLensLoading: boolean;
  // 事件处理
  refreshPageData: () => void;
  handleShowSizeGuide: () => void;
  handleViewMoreReviews: () => void;
  bottomButtonClick: (type: number, btnName: string) => void;
  breadcrumbItems: Array<{ label: string; href: string }>;
  SD_TryONBtn_Exp: (btn_name: string, item: any, sdEventName: string) => void;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
  SDProductDetailsPageInteractionBtnClick?: (
    params: IProductDetailsPageInteractionBtnClickParams,
  ) => void;
  SDProductDetailsPageRelatedMktExp?: (params: any, index: number) => void;
  SDProductDetailsPageRelatedMktClick?: (params: any, index: number) => void;
  SDProductDetailsPageProductPicExp?: (progress: number) => void;
  clientRenderReady: boolean;
  promotionScheduleData?: any;
  isBot?: boolean;
}

const WapGoodsDetail: React.FC<WapGoodsDetailProps> = ({
  refreshPageData,
  detail,
  showTabBtn,
  reviewsData,
  featuredReview,
  reviewImagesRes,
  shapePath,
  materialPath,
  getGlassesFilter,
  reviewDialog,
  setReviewDialog,
  addCartLoading,
  frameOnlyLoading,
  selectLensLoading,
  handleShowSizeGuide,
  handleViewMoreReviews,
  bottomButtonClick,
  breadcrumbItems,
  SD_TryONBtn_Exp,
  SDProductDetailsPageBtnClick,
  SDProductDetailsPageInteractionBtnClick,
  SDProductDetailsPageRelatedMktExp,
  SDProductDetailsPageRelatedMktClick,
  SDProductDetailsPageProductPicExp,
  clientRenderReady,
  promotionScheduleData,
  isBot = false,
}) => {
  // const [product] = useState(detail?.product);
  const product = detail?.product;
  const tryOnAction = useStore((state) => state.handleTryon);
  const handleTryOn = useCallback(() => {
    tryOnAction(product);
  }, [product]);
  const t = useTranslations('goodsDetail');

  // 简化版：只管理收藏状态和数量的变化
  const [wishState, setWishState] = useState({
    is_wish: detail?.customer?.is_wish || 2,
    wish_num: product?.wish_num || 0,
  });
  const [reviewSectionReady, setReviewSectionReady] = useState(isBot);

  // 同步外部 detail 的变化
  useEffect(() => {
    setWishState({
      is_wish: detail?.customer?.is_wish || 2,
      wish_num: product?.wish_num || 0,
    });
  }, [detail?.customer?.is_wish, product?.wish_num]);

  useEffect(() => {
    if (!isBot) {
      setReviewSectionReady(true);
    }
  }, [isBot]);

  // 处理收藏状态变化
  const handleWishChange = useCallback((isWish: number, delta: number) => {
    setWishState((prev) => ({
      is_wish: isWish,
      wish_num: Math.max(0, prev.wish_num + delta),
    }));
  }, []);

  // 判断是否是饰品：is_glass !== 1 表示是饰品
  const isAccessory = product.is_glass !== 1;

  // 根据AB实验字段和商品类型选择组件
  // 如果 product.ab_test.product_detail_page_decision_area_test_m === 1 或者商品是饰品，使用 ProductInfo (m.tsx)
  // 否则使用 ProductInfoGlasses (glasses.m.tsx)
  const shouldUseProductInfoM =
    (product as any)?.ab_test?.product_detail_page_decision_area_test_m === 1 || isAccessory;
  // let shouldUseProductInfoM = false;

  // 获取 AB 实验字段 product_detail_page_detail_area_test_m
  // 1: 默认完全展开
  // 2: 默认完全折叠
  // undefined/null/0: 默认完全展开（没有 AB 实验时）
  const detailAreaTestM = (product as any)?.ab_test?.product_detail_page_detail_area_test_m;
  // let detailAreaTestM = 1;

  // 获取 AB 实验字段 product_detail_page_review_area_test_m
  // 1: 商品评论在图文介绍之前（当前排列）
  // 2: 商品评论在图文介绍的下边
  const reviewAreaTestM = (product as any)?.ab_test?.product_detail_page_review_area_test_m;
  // let reviewAreaTestM = 1;

  // 计算默认展开状态
  const getDefaultExpandState = () => {
    if (detailAreaTestM === 1) {
      return true; // 默认展开
    } else if (detailAreaTestM === 2) {
      return false; // 默认合起来
    }
    // 如果没有 AB 实验字段，默认完全展开
    return true;
  };

  const renderBottomButton = () => {
    // 直接在渲染时创建更新的 detail，避免额外的 useMemo
    const updatedDetail = {
      ...detail,
      product: { ...detail.product, wish_num: wishState.wish_num },
      customer: { ...detail.customer, is_wish: wishState.is_wish },
    };

    return (
      <BottomButton
        detail={updatedDetail}
        bottomButtonClick={bottomButtonClick}
        addCartLoading={addCartLoading}
        frameOnlyLoading={frameOnlyLoading}
        selectLensLoading={selectLensLoading}
        SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
        clientRenderReady={clientRenderReady}
        onWishChange={handleWishChange}
      />
    );
  };

  const PopupComponent = isBot ? Popup : PopupClientOnly;
  const CustomerReviewsComponent = isBot ? CustomerReviews : CustomerReviewsClientOnly;
  const CustomerReviewsListComponent = isBot ? CustomerReviewsList : CustomerReviewsListClientOnly;
  const LensesAndCoatingsWithPopupComponent = isBot
    ? LensesAndCoatingsWithPopup
    : LensesAndCoatingsWithPopupClientOnly;
  const ServicesGuaranteesComponent = isBot ? ServicesGuarantees : ServicesGuaranteesClientOnly;
  const ProductDetailsWithPopupComponent = isBot
    ? ProductDetailsWithPopup
    : ProductDetailsWithPopupClientOnly;
  const FreeGiftComponent = isBot ? FreeGift : FreeGiftClientOnly;
  const ProductDescriptionCollapseComponent = isBot
    ? ProductDescriptionCollapse
    : ProductDescriptionCollapseClientOnly;
  const ProductRichTextComponent = isBot ? ProductRichText : ProductRichTextClientOnly;
  const renderCustomerReviews = () => {
    // if (!reviewsData?.list?.length) {
    //   return null;
    // }

    if (!isBot && !reviewSectionReady) {
      return <CustomerReviewsLoadingSkeleton />;
    }

    return (
      <CustomerReviewsComponent
        detail={detail}
        reviewsData={reviewsData}
        featuredReview={featuredReview as any}
        isGlass={product.is_glass === 1}
        onViewMore={handleViewMoreReviews}
        SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
        productId={product.id}
        shopSku={product.shop_sku}
        reviewImagesRes={reviewImagesRes}
        renderBottomButton={renderBottomButton}
        hasOutOfStockBanner={product?.status !== 2 && (product?.qty || 0) < 1}
      />
    );
  };

  return (
    // 这里设置 relative 是为了在手机safari浏览器上让推荐商品组件不遮挡底部按钮
    <Box
      className="goods-detail-client relative z-[99]"
      sx={{ minHeight: '60vh', width: '100vw', overflow: 'hidden' }}
    >
      {/* 面包屑导航 */}
      <Box className="relative">
        <Breadcrumbs
          items={breadcrumbItems}
          className="absolute top-[14px] left-[10px] z-10 text-[12px]"
          SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
        />

        {/* 商品轮播图 */}
        <TopCarousel
          productDetail={product}
          showTabBtn={showTabBtn}
          showTryOn
          isGoodsDetail
          onTryOn={() => {
            handleTryOn();
            SDProductDetailsPageBtnClick?.('TRY ON');
          }}
          SD_TryONBtn_Exp={SD_TryONBtn_Exp}
          SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
        />
      </Box>
      {/* 商品基本信息 */}
      {shouldUseProductInfoM ? (
        <ProductInfo
          refreshPageData={refreshPageData}
          product={product}
          countDown={detail?.product?.count_down}
          getGlassesFilter={getGlassesFilter}
          onShowSizeGuide={handleShowSizeGuide}
          SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
          SDProductDetailsPageRelatedMktExp={SDProductDetailsPageRelatedMktExp}
          SDProductDetailsPageRelatedMktClick={SDProductDetailsPageRelatedMktClick}
          clientRenderReady={clientRenderReady}
          promotionScheduleData={promotionScheduleData}
        />
      ) : (
        <ProductInfoGlasses
          refreshPageData={refreshPageData}
          product={product}
          countDown={detail?.product?.count_down}
          getGlassesFilter={getGlassesFilter}
          onShowSizeGuide={handleShowSizeGuide}
          SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
          SDProductDetailsPageRelatedMktExp={SDProductDetailsPageRelatedMktExp}
          SDProductDetailsPageRelatedMktClick={SDProductDetailsPageRelatedMktClick}
          clientRenderReady={clientRenderReady}
          reviewsData={reviewsData}
          abTestField1={(product as any)?.ab_test?.product_detail_page_decision_area_test_m}
          promotionScheduleData={promotionScheduleData}
        />
      )}
      {/* 商品评论 - 当 reviewAreaTestM !== 2 时显示在这里 */}
      {reviewAreaTestM !== 2 && renderCustomerReviews()}
      {/* 商品评论列表弹窗 */}
      <PopupComponent
        open={reviewDialog}
        onClose={() => setReviewDialog(false)}
        disableScrollLock
        position="right"
        width="100%"
        round={false}
        bodyClassName="bg-[#f4f4f4]"
        title={
          <Box
            className="popup-title"
            sx={{
              padding: '16px',
              borderBottom: '1px solid #f4f4f4',
              textAlign: 'center',
              position: 'relative',
            }}
          >
            <Typography
              component="div"
              className="popup-title-text"
              sx={{
                fontSize: '16px',
                color: colors.primaryBlack,
                fontFamily: font.be,
              }}
            >
              {t('customerReivewsList.reviews')}
            </Typography>
            <IconButton
              onClick={() => setReviewDialog(false)}
              sx={{
                position: 'absolute',
                left: 12,
                top: 15,
                padding: '4px',
              }}
              size="small"
            >
              <LeftArrowOutlined className="text-[24px] text-primary-text" />
            </IconButton>
          </Box>
        }
      >
        <CustomerReviewsListComponent
          reviewsData={reviewsData}
          product={product}
          isGlass={product.is_glass === 1}
          SDProductDetailsPageInteractionBtnClick={SDProductDetailsPageInteractionBtnClick}
        />
        {renderBottomButton()}
      </PopupComponent>
      <Box className="px-14 mx-auto">
        {product.is_glass === 1 && (
          <>
            {/* 镜片选择 */}
            <LensesAndCoatingsWithPopupComponent
              detail={product}
              defaultOpen={getDefaultExpandState()}
              showQuestionIcon
              SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
            />
            {/* 服务保障 */}
            <ServicesGuaranteesComponent
              detail={product}
              defaultOpen={getDefaultExpandState()}
              SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
            />
            {/* 商品参数 */}
            <ProductDetailsWithPopupComponent
              detail={product}
              shapePath={shapePath}
              materialPath={materialPath}
              defaultOpen={getDefaultExpandState()}
              SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
            />
            {/* Free Gift */}
            <FreeGiftComponent
              detail={product}
              defaultOpen={getDefaultExpandState()}
              SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
            />
          </>
        )}
        {product.is_glass === 2 && (
          <>
            {/* 服务保障 */}
            <ServicesGuaranteesComponent
              detail={product}
              defaultOpen={getDefaultExpandState()}
              SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
            />
            {product.show_description && (
              <ProductDescriptionCollapseComponent
                detail={product}
                defaultOpen={getDefaultExpandState()}
                SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
              />
            )}
          </>
        )}
      </Box>
      {/* 商品图文介绍 */}
      {product.is_glass === 1 && (
        <ProductRichTextComponent
          detail={product}
          defaultOpen={getDefaultExpandState()}
          featureDescriptionDefaultOpen={getDefaultExpandState()}
          SDProductDetailsPageProductPicExp={SDProductDetailsPageProductPicExp}
          SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
        />
      )}
      {/* 商品评论 - 当 reviewAreaTestM === 2 时显示在这里（图文介绍下边） */}
      {reviewAreaTestM === 2 && renderCustomerReviews()}
      {/* 底部按钮 */}
      {renderBottomButton()}
    </Box>
  );
};

export default WapGoodsDetail;
